import type { JobOffer, Pavilion, Stand } from '@vv/common';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { paths } from '../routes';
import { formatDate } from '../utils/format';
import { accentBackground, accentFor } from '../utils/pavilion-accent';

import { CarouselDots, useCarouselPosition } from './CarouselDots';
import { MediaCarousel } from './MediaCarousel';
import { ShareButton } from './ShareButton';
import { StandLogo } from './StandLogo';

export interface StandPreviewCardProps {
  stand: Stand;
  pavilion: Pavilion;
  /**
   * Passed in rather than fetched here: the pavilion listing renders one card per
   * exhibitor, so resolving offers inside the card would be one request per card.
   */
  jobs: JobOffer[];
}

/** The pavilion listing entry: identity, media carousel and a job carousel. */
export function StandPreviewCard({ stand, pavilion, jobs }: StandPreviewCardProps): ReactElement {
  const { t } = useTranslation();
  const accent = accentBackground(accentFor(pavilion.position));
  const {
    trackRef: jobsTrackRef,
    onScroll: onJobsScroll,
    active: activeJob,
    goTo: goToJob,
  } = useCarouselPosition();

  return (
    <article className="space-y-3">
      <header className="flex items-center gap-3">
        <Link
          to={paths.exhibitor(stand.slug, { from: pavilion.slug })}
          className="flex min-w-0 items-center gap-3"
        >
          <StandLogo url={stand.logoUrl} className="size-11" />
          <h2 className="truncate font-bold text-fog-100">{stand.title}</h2>
        </Link>

        {stand.chatEnabled && (
          <span className="shrink-0 rounded-full bg-white/10 px-3 py-1 text-xs text-online">
            {t('stand.chatAvailable')}
          </span>
        )}

        <ShareButton
          title={stand.title}
          path={paths.exhibitor(stand.slug)}
          contentType="exhibitor"
          itemId={stand.id}
          className="ml-auto"
        />
      </header>

      <MediaCarousel items={stand.media} />

      {jobs.length > 0 && (
        <div>
          <div ref={jobsTrackRef} onScroll={onJobsScroll} className="carousel pt-1">
            {jobs.map((job) => (
              <Link
                key={job.id}
                to={paths.job(stand.slug, job.id, pavilion.slug)}
                className="card relative w-[86%] max-w-[330px] overflow-hidden py-4 pr-4 pl-5 transition-colors hover:bg-ink-800"
              >
                <span
                  aria-hidden="true"
                  className={`absolute top-4 bottom-4 left-0 w-1 rounded-r ${accent}`}
                />
                <h3 className="font-bold text-fog-100">{job.title}</h3>
                {job.location !== null && (
                  <p className="mt-1 text-sm text-fog-500">
                    {t('job.location')} {job.location}
                  </p>
                )}
                {job.deadline !== null && (
                  <p className="text-sm text-fog-500">
                    {t('job.deadline')} {formatDate(job.deadline)}
                  </p>
                )}
              </Link>
            ))}
          </div>
          <CarouselDots count={jobs.length} active={activeJob} onSelect={goToJob} />
        </div>
      )}

      <nav className="flex flex-wrap gap-2 pt-1">
        {(['jobs', 'why', 'gallery', 'chat'] as const)
          .filter((tab) => tab !== 'chat' || stand.hasChat)
          .map((tab) => (
            <Link
              key={tab}
              to={paths.exhibitor(stand.slug, { tab, from: pavilion.slug })}
              className="relative rounded-lg bg-ink-850 px-3 py-2 text-sm text-fog-300 transition-colors hover:bg-ink-800"
            >
              {t(`stand.tabs.${tab}`)}
              {tab === 'chat' && stand.chatOnline && (
                <span
                  aria-hidden="true"
                  className="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-online"
                />
              )}
            </Link>
          ))}
      </nav>
    </article>
  );
}
